import { Feature, Point, Position } from "geojson";

import { getEndVertex } from "fond/utils/feature";

/**
 * In electrical networks, the 'phase' of a cable or transformer refers to the specific electrical phases
 * (A, B, or C) that the component is associated with.
 *
 * We can override the solver's default choice of 'phase' at certain places in the network. These override
 * locations currently consist of:
 *
 * (i) the first node of a single-phase branch leaving a >=2-phase trunk, and
 * (ii) single-phase transformers sitting on a >=2-phase trunk.
 *
 */

export const PHASE_DECISION_TYPES = {
  branchRoot: "1PH_BRANCH_ROOT",
  transformer: "TRUNK_TRANSFORMER",
} as const;
export type PhaseDecisionType = (typeof PHASE_DECISION_TYPES)[keyof typeof PHASE_DECISION_TYPES];

export const SINGLE_PHASES = ["A", "B", "C"] as const;
export type SinglePhase = (typeof SINGLE_PHASES)[number];

export interface PhaseDecisionProperties {
  Phase: SinglePhase;
  DecisionType: PhaseDecisionType;
  FondID: string;
  [key: string]: unknown;
}

export interface PhaseNetwork {
  /**
   * Radial primary wire edges.
   *
   * Electrical networks are typically 'radial'; power flows outwards from a source. Electrical networks are not
   * necessarily a tree however: they can contain loops and/or virtual edges. We say an edge is 'radial' if it is
   * not a loop or virtual edge. Phase decisions are only made on radial edges.
   */
  radialEdges: Feature[];

  /**
   * Phase decision points.
   */
  decisionPoints: Feature<Point, PhaseDecisionProperties>[];

  /**
   * A map from each radial edge to the decision point that determines its phase.
   */
  branchRootByEdgeId: Map<string, string>;
}

export const isSinglePhase = (value: unknown): value is SinglePhase => SINGLE_PHASES.includes(value as SinglePhase);

const isTruthyFlag = (value: unknown): boolean => value === "True" || value === true;

const numPhases = (edge: Feature): number => Number(edge.properties?.NumPhases ?? 0);

/** Return primary wire edges that are 'radial'. */
const radialPrimaryEdges = (primaryWires: Feature[]): Feature[] =>
  primaryWires.filter((edge) => !isTruthyFlag(edge.properties?.IsLoop) && !isTruthyFlag(edge.properties?.IsVirtual));

/**
 * Return a function that returns the "head phase count" of a given node.
 *
 * The "head phase count" of a node is the highest phase count among the radial edges feeding it.
 * Every source node is treated as if it has a head phase count of 3, even if no 3-phase wire exists.
 */
const buildHeadPhases = (radialEdges: Feature[], sources: Feature[]): ((nodeId: string) => number) => {
  const maxIncomingPhases = new Map<string, number>();
  for (const edge of radialEdges) {
    const endId = edge.properties?.EndID;
    if (typeof endId === "string") {
      maxIncomingPhases.set(endId, Math.max(maxIncomingPhases.get(endId) ?? 0, numPhases(edge)));
    }
  }

  const sourceIds = new Set(sources.map((source) => source.properties?.FondID).filter((id): id is string => typeof id === "string"));

  return (nodeId) => (sourceIds.has(nodeId) ? 3 : (maxIncomingPhases.get(nodeId) ?? 0));
};

const decisionPoint = (
  nodeId: string,
  phase: SinglePhase,
  decisionType: PhaseDecisionType,
  coordinates: Position
): Feature<Point, PhaseDecisionProperties> => ({
  type: "Feature",
  properties: { Phase: phase, DecisionType: decisionType, FondID: nodeId },
  geometry: { type: "Point", coordinates },
});

/**
 * Build the phase network from solver output layers.
 *
 * @param primaryWires - features of the `output/primary_wires` layer
 * @param transformers - features of the `output/transformers` layer
 * @param sources - features of the `output/sources` layer
 */
export const buildPhaseNetwork = (primaryWires: Feature[], transformers: Feature[], sources: Feature[]): PhaseNetwork => {
  const radialEdges = radialPrimaryEdges(primaryWires);
  const headPhases = buildHeadPhases(radialEdges, sources);

  const edgesByStart = new Map<string, Feature[]>();
  for (const edge of radialEdges) {
    const startId = edge.properties?.StartID;
    if (typeof startId === "string") {
      const edges = edgesByStart.get(startId);
      if (edges) {
        edges.push(edge);
      } else {
        edgesByStart.set(startId, [edge]);
      }
    }
  }

  const decisionsByNode = new Map<string, Feature<Point, PhaseDecisionProperties>>();
  const branchRootByEdgeId = new Map<string, string>();

  const tagEdge = (edge: Feature, rootId: string) => {
    const fondId = edge.properties?.FondID;
    if (typeof fondId === "string") branchRootByEdgeId.set(fondId, rootId);
  };

  for (const edge of radialEdges) {
    const { StartID: startId, EndID: endId, Phase: phase } = edge.properties ?? {};
    // case (i): the first node of a single-phase branch leaving a >=2-phase trunk
    const startsBranch =
      typeof startId === "string" && typeof endId === "string" && isSinglePhase(phase) && numPhases(edge) === 1 && headPhases(startId) >= 2;

    // eslint-disable-next-line no-continue
    if (!startsBranch) continue;

    const coordinates = getEndVertex(edge);
    // eslint-disable-next-line no-continue
    if (!coordinates) continue;

    // Create a new decision point at the branch point
    decisionsByNode.set(endId, decisionPoint(endId, phase, PHASE_DECISION_TYPES.branchRoot, coordinates));

    // Tag the branch's first edge (and every downstream single-phase edge) as belonging to the branch's root node.
    tagEdge(edge, endId);
    const stack = [endId];
    const seen = new Set<string>(stack);
    while (stack.length > 0) {
      const current = stack.pop()!;
      for (const downstream of edgesByStart.get(current) ?? []) {
        const downstreamEndId = downstream.properties?.EndID;
        if (numPhases(downstream) === 1 && typeof downstreamEndId === "string" && !seen.has(downstreamEndId)) {
          seen.add(downstreamEndId);
          tagEdge(downstream, endId);
          stack.push(downstreamEndId);
        }
      }
    }
  }

  for (const transformer of transformers) {
    const { FondID: fondId, Phase: phase } = transformer.properties ?? {};
    const geometry = transformer.geometry.type === "Point" ? transformer.geometry : undefined;
    // case (2): single phase transformers sitting on a >=2-phase trunk
    if (typeof fondId === "string" && isSinglePhase(phase) && geometry && headPhases(fondId) >= 2) {
      decisionsByNode.set(fondId, decisionPoint(fondId, phase, PHASE_DECISION_TYPES.transformer, geometry.coordinates));
    }
  }

  // ensure decision points are deterministic
  const decisionPoints = [...decisionsByNode.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, feature]) => feature);

  return { radialEdges, decisionPoints, branchRootByEdgeId };
};

/**
 * Computes the phase each radial primary wire is expected to carry once the phase
 * overrides are applied.
 *
 * @param network - the phase network derived from the solve output
 * @param overridePoints - the current phase override point features
 * @returns feature id -> phase for the wire
 */
export const computePreviewPhases = (network: PhaseNetwork, overridePoints: Feature[]): Map<string | number, SinglePhase> => {
  const overridesByNode = new Map<string, SinglePhase>();
  for (const point of overridePoints) {
    const { FondID: fondId, Phase: phase } = point.properties ?? {};
    if (typeof fondId === "string" && isSinglePhase(phase)) {
      overridesByNode.set(fondId, phase);
    }
  }

  // transport the phase from each override point to every radial edge downstream of it
  const previewPhases = new Map<string | number, SinglePhase>();
  for (const edge of network.radialEdges) {
    const fondId = edge.properties?.FondID;
    const branchRootId = typeof fondId === "string" ? network.branchRootByEdgeId.get(fondId) : undefined;
    const overridePhase = branchRootId ? overridesByNode.get(branchRootId) : undefined;
    if (edge.id != null && overridePhase && overridePhase !== edge.properties?.Phase) {
      previewPhases.set(edge.id, overridePhase);
    }
  }
  return previewPhases;
};
